export const PAGES = {
  "booking-policy": {
    slug: "booking-policy",
    path: "/booking-policy",
    title: "Booking Policy",
    seoDescription: `Review the booking policy for appointments at Lunar Ambitions in Orlando, FL. Covers deposits, scheduling requirements, and what to expect when you book a facial or skin care treatment.`,
    blocks: [
      { type: "h2", text: `Appointments` },
      {
        type: "p",
        text: `All appointments require a non-refundable deposit at the time of booking. Your deposit goes toward your treatment total, with the remaining balance due at the time of service.`,
      },
      { type: "h2", text: `Cancellations and Rescheduling` },
      {
        type: "p",
        text: `Please cancel or reschedule at least 24 hours before your appointment. Cancellations made less than 24 hours in advance will result in forfeiture of your $25 deposit. To cancel or reschedule, either follow the link in your confirmation email or contact Alena directly at (407) 538-9402 or alena@lunarambitions.com.`,
        links: [
          { match: `(407) 538-9402`, href: `tel:+14075389402`, testId: `booking-policy-phone-link` },
          { match: `alena@lunarambitions.com`, href: `mailto:alena@lunarambitions.com`, testId: `booking-policy-email-link` },
        ],
      },
      { type: "h2", text: `No-Shows` },
      {
        type: "p",
        text: `Clients who do not show up without prior notice will forfeit their $25 deposit. Repeated no-shows may require full prepayment for future appointments.`,
      },
      { type: "h2", text: `Late Arrivals` },
      {
        type: "p",
        text: `Arriving more than 15 minutes late may result in a shortened service or the need to reschedule. The full service fee still applies regardless of arrival time.`,
      },
      { type: "h2", text: `Refunds` },
      {
        type: "p",
        text: `Due to the nature of aesthetic services, all sales are final once a service has been completed. If you have concerns following your treatment, please reach out within 48 hours by phone or email and every effort will be made to address them.`,
      },
      { type: "h2", text: `Treatment Eligibility` },
      {
        type: "p",
        text: `Please disclose all relevant medical conditions, allergies, medications, active skin conditions, pregnancy, or recent cosmetic procedures before your appointment. Certain treatments may be declined if they are not considered safe or appropriate for your current health status.`,
      },
      { type: "h2", text: `Results` },
      {
        type: "p",
        text: `Results vary depending on skin type, lifestyle, home care, and consistency of treatments. No specific outcome can be guaranteed.`,
      },
      { type: "h2", text: `Children and Guests` },
      {
        type: "p",
        text: `To maintain a relaxing environment for all clients, please avoid bringing children or additional guests to your appointment unless previously approved.`,
      },
      { type: "h2", text: `Intake Form` },
      {
        type: "p",
        text: `All new clients are required to complete an intake form before their first appointment. A link will be provided after booking. Returning clients should notify Alena of any changes to their health or skin care routine before each visit.`,
      },
      { type: "h2", text: `By Booking` },
      {
        type: "p",
        text: `Scheduling an appointment confirms that you have read and agree to these policies.`,
      },
    ],
  },
  "cancellation-policy": {
    slug: "cancellation-policy",
    path: "/cancellation-policy",
    title: "Cancellation Policy",
    seoTitle: `Cancellation Policy | Lunar Ambitions | Orlando, FL`,
    seoDescription: `Review the cancellation policy for facial and microneedling appointments at Lunar Ambitions in Orlando, FL. Covers rescheduling, no-shows, late arrivals, and same-day booking terms.`,
    blocks: [
      {
        type: "p",
        text: `Your time is valued here, and the same is asked in return. Please take a moment to review this policy before booking your appointment.`,
      },
      { type: "h2", text: `Appointment Cancellations and Rescheduling` },
      {
        type: "p",
        text: `If you need to cancel or reschedule, 24 hours' notice is required. Cancellations made with less than 24 hours' notice, or missed appointments, will result in forfeiture of your $25 deposit. To cancel or reschedule, reach out by phone at (407) 538-9402 or through the online booking system at https://book.squareup.com/appointments/lur0h9l47kubxi/location/LHNNP5Y1WCSHS/services`,
        links: [
          { match: `(407) 538-9402`, href: `tel:+14075389402`, testId: `cancellation-policy-phone-link` },
          {
            match: `https://book.squareup.com/appointments/lur0h9l47kubxi/location/LHNNP5Y1WCSHS/services`,
            href: `https://book.squareup.com/appointments/lur0h9l47kubxi/location/LHNNP5Y1WCSHS/services`,
            external: true,
            testId: `cancellation-policy-booking-link`,
          },
        ],
      },
      { type: "h2", text: `No-Shows` },
      {
        type: "p",
        text: `If you miss your appointment without prior notice, your $25 deposit will be forfeited. Repeated no-shows may require prepayment for future services or result in loss of booking privileges.`,
      },
      { type: "h2", text: `Late Arrivals` },
      {
        type: "p",
        text: `If you arrive late, your service time may be shortened out of respect for clients scheduled after you. The full service fee still applies. If you are more than 15 minutes late, your appointment may need to be rescheduled and a cancellation fee may apply.`,
      },
      { type: "h2", text: `Same-Day Bookings` },
      {
        type: "p",
        text: `Same-day appointments are subject to the same cancellation policy. By booking, you agree to these terms.`,
      },
      { type: "h2", text: `Illness and Emergencies` },
      {
        type: "p",
        text: `Not feeling well? Please stay home. Reach out as soon as possible and every effort will be made to get you rescheduled.`,
      },
      { type: "h2", text: `Special Circumstances` },
      {
        type: "p",
        text: `Life happens and cancellation fees are reviewed on a case-by-case basis for genuine emergencies. Just reach out directly.`,
      },
      { type: "h2", text: `Confirmation and Reminders` },
      {
        type: "p",
        text: `You will receive a confirmation and reminder before your appointment. Please confirm or cancel within the required window.`,
      },
      { type: "h2", text: `Contact` },
      {
        type: "lines",
        lines: [
          `Alena - Licensed Esthetician / Owner`,
          { text: `alena@lunarambitions.com`, links: [{ match: `alena@lunarambitions.com`, href: `mailto:alena@lunarambitions.com`, testId: `cancellation-policy-email-link` }] },
          { text: `(407) 538-9402`, links: [{ match: `(407) 538-9402`, href: `tel:+14075389402`, testId: `cancellation-policy-contact-phone-link` }] },
        ],
      },
    ],
  },
  "location-and-directions": {
    slug: "location-and-directions",
    path: "/location-and-directions",
    title: "Location and Directions",
    seoTitle: `Location and Directions | Lunar Ambitions | Orlando, FL`,
    seoDescription: `Find Lunar Ambitions esthetician studio in Orlando, FL. Get directions and location details before booking a facial, microneedling, or skin care appointment.`,
    blocks: [
      {
        type: "p",
        text: `Lunar Ambitions is located in Orlando, Florida. I invite you to visit my studio for a personalized esthetics experience.`,
      },
      { type: "h2", text: `The Studio` },
      {
        type: "lines",
        lines: [
          `Lunar Ambitions`,
          `Orlando, FL`,
          { text: `Phone: (407) 538-9402`, links: [{ match: `(407) 538-9402`, href: `tel:+14075389402`, testId: `location-studio-phone-link` }] },
        ],
      },
      {
        type: "p",
        text: `Please contact me at (407) 538-9402 or book online for the exact suite location and any directions specific to your starting point.`,
        links: [{ match: `(407) 538-9402`, href: `tel:+14075389402`, testId: `location-contact-phone-link` }],
      },
      { type: "h2", text: `Hours of Operation` },
      {
        type: "p",
        text: `Hours vary by appointment availability. Please book online or call to confirm current availability before your visit.`,
      },
      { type: "h2", text: `Book Your Appointment` },
      {
        type: "p",
        text: `All services are by appointment only. Walk-ins are not accepted at this time. To schedule your visit, use the online booking system or call me directly.`,
      },
      {
        type: "cta",
        label: `Book Online`,
        href: `https://book.squareup.com/appointments/lur0h9l47kubxi/location/LHNNP5Y1WCSHS/services`,
        testId: `location-book-online-button`,
      },
      {
        type: "p",
        text: `Phone: (407) 538-9402`,
        links: [{ match: `(407) 538-9402`, href: `tel:+14075389402`, testId: `location-book-phone-link` }],
      },
      { type: "h2", text: `Parking` },
      {
        type: "p",
        text: `Parking is available on-site. Additional parking details will be provided when your appointment is confirmed.`,
      },
      { type: "h2", text: `What to Expect Upon Arrival` },
      {
        type: "p",
        text: `Please arrive 5 to 10 minutes before your scheduled appointment time to allow time to complete a brief intake form if needed. Late arrivals may result in a shortened service time out of respect for clients scheduled after you.`,
      },
      {
        type: "p",
        text: `If you are running late, please call me as soon as possible at (407) 538-9402.`,
        links: [{ match: `(407) 538-9402`, href: `tel:+14075389402`, testId: `location-late-phone-link` }],
      },
      { type: "h2", text: `Need Help Finding the Studio?` },
      {
        type: "p",
        text: `I am happy to guide you. Reach out at (407) 538-9402 or send an email at alena@lunarambitions.com.`,
        links: [
          { match: `(407) 538-9402`, href: `tel:+14075389402`, testId: `location-help-phone-link` },
          { match: `alena@lunarambitions.com`, href: `mailto:alena@lunarambitions.com`, testId: `location-help-email-link` },
        ],
      },
      { type: "p", text: `I look forward to welcoming you.` },
    ],
  },
  "terms-and-conditions": {
    slug: "terms-and-conditions",
    path: "/terms-and-conditions",
    title: "Terms and Conditions",
    seoTitle: `Terms and Conditions | Lunar Ambitions | Orlando, FL`,
    seoDescription: `Review the terms and conditions for booking esthetic services at Lunar Ambitions in Orlando, FL. Covers appointment policies, cancellations, client responsibilities, and service agreements.`,
    blocks: [
      { type: "meta", text: `Effective Date: April 16, 2026` },
      { type: "meta", text: `Last Updated: May 21, 2026` },
      {
        type: "p",
        text: `Please read these Terms and Conditions carefully before using the services of Lunar Ambitions. By booking an appointment or using my services, you agree to be bound by these terms.`,
      },
      { type: "h2", text: `1. Services` },
      {
        type: "p",
        text: `Lunar Ambitions provides professional esthetics and facial treatment services in Orlando, Florida. All services are performed by a licensed esthetician. Results from services may vary based on individual skin type, health conditions, and adherence to aftercare instructions.`,
      },
      { type: "h2", text: `2. Appointments and Booking` },
      {
        type: "p",
        text: `Appointments may be booked through my online booking system. By scheduling an appointment, you confirm that you are 18 years of age or older, or have parental/guardian consent. You agree to provide accurate and complete information when booking.`,
      },
      { type: "h2", text: `3. Health and Safety` },
      {
        type: "p",
        text: `For the safety of my clients, you must disclose any known skin conditions, allergies, medical conditions, medications, or recent procedures prior to your service. Lunar Ambitions reserves the right to decline or modify a service if I determine it may pose a risk to your health or safety. Clients who arrive ill or showing signs of a contagious condition may have their appointment rescheduled. I reserve the right to refuse service to anyone for health or safety reasons.`,
      },
      { type: "h2", text: `4. Cancellation and No-Show Policy` },
      {
        type: "p",
        text: `I require a minimum of 24 hours' notice to cancel or reschedule an appointment. Cancellations made with less than 24 hours' notice, or failure to appear for a scheduled appointment, will result in forfeiture of your $25 deposit. Please review my full Cancellation Policy for details.`,
        links: [{ match: `Cancellation Policy`, href: `/cancellation-policy`, testId: `terms-cancellation-policy-link` }],
      },
      { type: "h2", text: `5. Results and Liability` },
      {
        type: "p",
        text: `While I strive to provide exceptional results, Lunar Ambitions makes no guarantees regarding specific outcomes. Skincare results are highly individual and depend on many factors outside my control. Lunar Ambitions is not liable for any adverse reactions resulting from undisclosed conditions or medications.`,
      },
      { type: "h2", text: `6. Payments` },
      {
        type: "p",
        text: `Payment is due at the time of service. I accept major credit cards and other payment methods as indicated at my studio. Prices are subject to change without notice.`,
      },
      { type: "h2", text: `7. Personal Property` },
      {
        type: "p",
        text: `Lunar Ambitions is not responsible for any lost, stolen, or damaged personal property while on my premises.`,
      },
      { type: "h2", text: `8. Photography and Marketing` },
      {
        type: "p",
        text: `With your prior written consent, I may use before-and-after photos for marketing purposes. You will never be photographed without explicit consent.`,
      },
      { type: "h2", text: `9. Intellectual Property` },
      {
        type: "p",
        text: `All content on my website, including text, graphics, logos, and images, is the property of Lunar Ambitions and is protected by applicable intellectual property laws. You may not reproduce or distribute my content without written permission.`,
      },
      { type: "h2", text: `10. Limitation of Liability` },
      {
        type: "p",
        text: `To the fullest extent permitted by law, Lunar Ambitions shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of my services or website.`,
      },
      { type: "h2", text: `11. Governing Law` },
      {
        type: "p",
        text: `These Terms and Conditions are governed by the laws of the State of Florida. Any disputes shall be resolved in the appropriate courts of Orange County, Florida.`,
      },
      { type: "h2", text: `12. Changes to These Terms` },
      {
        type: "p",
        text: `I reserve the right to update these Terms and Conditions at any time. Changes will be posted on this page with an updated effective date. Continued use of my services constitutes acceptance of the revised terms.`,
      },
      { type: "h2", text: `13. Contact` },
      {
        type: "p",
        text: `If you have questions about these Terms and Conditions, please contact me:`,
      },
      {
        type: "lines",
        lines: [
          `Lunar Ambitions`,
          `Orlando, Florida`,
          { text: `Phone: (407) 538-9402`, links: [{ match: `(407) 538-9402`, href: `tel:+14075389402`, testId: `terms-phone-link` }] },
          { text: `Email: alena@lunarambitions.com`, links: [{ match: `alena@lunarambitions.com`, href: `mailto:alena@lunarambitions.com`, testId: `terms-email-link` }] },
        ],
      },
    ],
  },
  "privacy-policy": {
    slug: "privacy-policy",
    path: "/privacy-policy",
    title: "Privacy Policy",
    seoTitle: `Privacy Policy | Lunar Ambitions | Orlando, FL`,
    seoDescription: `Lunar Ambitions is committed to protecting your personal and health information. Review the privacy policy to understand how client data is collected, used, and safeguarded when booking esthetic services in Orlando, FL.`,
    blocks: [
      { type: "meta", text: `Effective Date: April 1, 2025` },
      { type: "meta", text: `Last Updated: May 21, 2025` },
      {
        type: "p",
        text: `Lunar Ambitions ("I," "my," "we," "our," or "us") is an esthetics studio located in Orlando, Florida. I am committed to protecting the privacy of my clients and website visitors. This Privacy Policy explains how I collect, use, and safeguard your information when you visit my website or book my services.`,
      },
      { type: "h2", text: `1. Information I Collect` },
      {
        type: "p",
        text: `I may collect the following types of information: Personal Information: Name, email address, phone number, and any details you provide when booking an appointment or contacting me. Health and Skin Information: Information you provide in intake forms related to skin conditions, allergies, or medical history that may affect your treatment. Usage Data: Information about how you interact with my website, including IP address, browser type, pages visited, and time spent on pages.`,
      },
      { type: "h2", text: `2. How I Use Your Information` },
      {
        type: "p",
        text: `I use the information I collect to: schedule and manage your appointments; communicate with you about your bookings, services, and promotions; provide a safe and personalized treatment experience; improve my website and services; comply with legal obligations; and respond to your inquiries.`,
      },
      { type: "h2", text: `3. Third-Party Services` },
      {
        type: "p",
        text: `I use Square Appointments, a third-party scheduling platform, to manage online bookings. When you book an appointment through my website, you will be directed to Square's platform, which has its own privacy policy. My website may also use analytics tools to understand visitor behavior. These services may collect anonymized data in accordance with their own privacy policies.`,
      },
      { type: "h2", text: `4. Cookies` },
      {
        type: "p",
        text: `My website uses cookies and similar technologies to enhance your browsing experience. Cookies are small text files stored on your device. You may adjust your browser settings to disable cookies; however, some features of my website may not function properly as a result.`,
      },
      { type: "h2", text: `5. Data Sharing` },
      {
        type: "p",
        text: `I do not sell, trade, or rent your personal information to third parties. I may share your information with trusted service providers who assist in operating my website or conducting our business, provided those parties agree to keep your information confidential. I may disclose your information when required by law or to protect my rights, property, or safety.`,
      },
      { type: "h2", text: `6. Data Retention` },
      {
        type: "p",
        text: `I retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, comply with legal obligations, and resolve any disputes.`,
      },
      { type: "h2", text: `7. Your Rights (Florida Residents)` },
      {
        type: "p",
        text: `As a Florida resident, you have the right to request access to the personal information I hold about you, request corrections to inaccurate information, and request deletion of your personal information, subject to certain legal exceptions. To make such a request, please contact me using the information below.`,
      },
      { type: "h2", text: `8. Data Security` },
      {
        type: "p",
        text: `I take reasonable measures to protect your personal information from unauthorized access, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and I cannot guarantee absolute security.`,
      },
      { type: "h2", text: `9. Children's Privacy` },
      {
        type: "p",
        text: `My services are intended for adults. I do not knowingly collect personal information from individuals under the age of 18. If you believe I have inadvertently collected such information, please contact me immediately.`,
      },
      { type: "h2", text: `10. Changes to This Policy` },
      {
        type: "p",
        text: `I may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. Continued use of my website or services after any changes constitutes your acceptance of the updated policy.`,
      },
      { type: "h2", text: `11. Contact` },
      {
        type: "p",
        text: `If you have any questions about this Privacy Policy, please contact me:`,
      },
      {
        type: "lines",
        lines: [
          `Lunar Ambitions`,
          `Orlando, Florida`,
          { text: `Phone: (407) 538-9402`, links: [{ match: `(407) 538-9402`, href: `tel:+14075389402`, testId: `privacy-phone-link` }] },
          { text: `Email: alena@lunarambitions.com`, links: [{ match: `alena@lunarambitions.com`, href: `mailto:alena@lunarambitions.com`, testId: `privacy-email-link` }] },
        ],
      },
    ],
  },
};
